import React, { useState, useRef, useEffect, useMemo } from "react";
import JSONPretty from "react-json-pretty";
import Styles from "./responseBox.module.scss";
import { testIdProps } from "../../../../../utils/testIdProps";

function prettifyKey(key: string) {
  // Remove known nested prefixes like result., metadata., etc.
  const cleanedKey = key.replace(/^(result|metadata|data|info|details)\./i, "");

  // Replace underscores/dashes with space
  const withSpaces = cleanedKey
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2"); // camelCase → camel Case

  // Capitalize each word
  return withSpaces
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

const isWalletBalanceError = (msg: string | undefined) =>
  !!msg &&
  msg.toLowerCase().includes("wallet balance is low");

function isLikelyImageUrl(url: string): boolean {
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(url);
}

function isHttpUrlString(v: unknown): v is string {
  return typeof v === "string" && /^https?:\/\//i.test(v.trim());
}

/** RC Verification Advance product ARN — PDF profile download only for this trial. */
const RC_VERIFICATION_ADVANCE_ARN = "in_vehicle_rc_advance";
const RC_REVERSE_VERIFICATION_ARN = "in_vehicle_rc_reverse";

/** Strips wrappers / NA; returns normalized base64 only if it decodes to a PDF. */
function extractPdfProfileBase64(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.toUpperCase() === "NA") return null;
  let raw = trimmed;
  if (raw.startsWith("data:")) {
    const b64Idx = raw.indexOf("base64,");
    if (b64Idx !== -1) raw = raw.slice(b64Idx + 7);
    else {
      const comma = raw.indexOf(",");
      raw = comma !== -1 ? raw.slice(comma + 1) : "";
    }
  }
  raw = raw.replace(/\s/g, "");
  if (!raw) return null;
  try {
    return atob(raw).includes("%PDF") ? raw : null;
  } catch {
    return null;
  }
}

function sanitizePdfDownloadFilename(name: string): string {
  const cleaned = name.replace(/[/\\?%*:|"<>]/g, "").trim() || "rc-profile";
  return cleaned.toLowerCase().endsWith(".pdf") ? cleaned : `${cleaned}.pdf`;
}

interface ResponseBoxProps {
  apiResponse: any;
  errorResponse?: string;
  toastTimeout: (show: boolean, type: string, message: string) => void;
  /** When true, Form tab shows e-stamp preview */
  stampCertificateMode?: boolean;
  /** Trial product ARN (e.g. in_vehicle_rc_advance) */
  serviceArn?: string;
  /** Postpaid orgs only — PDF profile actions */
  isPostpaidClient?: boolean;
}

function ResponseBox(props: ResponseBoxProps) {
  const [activeTab, setActiveTab] = useState<"form" | "dev">("dev");
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set());
  const responseBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.stampCertificateMode) {
      setActiveTab("form");
    }
  }, [props.stampCertificateMode]);

  const themes = { main: "font-size: 14px" };

  const parsedForView = useMemo(() => {
    try {
      if (!props.apiResponse) return null;
      return typeof props.apiResponse === "string"
        ? JSON.parse(props.apiResponse)
        : props.apiResponse;
    } catch {
      return null;
    }
  }, [props.apiResponse]);
  const hasParsedResponseData =
    parsedForView !== null &&
    parsedForView !== undefined &&
    !(typeof parsedForView === "string" && parsedForView.trim() === "");

  // API shape: { err, status, data: { orderId, stampCertificateUrl, orderItems: [...] } }
  const responseData =
    parsedForView &&
    typeof parsedForView === "object" &&
    !Array.isArray(parsedForView) &&
    (parsedForView as Record<string, unknown>).data != null &&
    typeof (parsedForView as Record<string, unknown>).data === "object" &&
    !Array.isArray((parsedForView as Record<string, unknown>).data)
      ? ((parsedForView as Record<string, unknown>).data as Record<string, any>)
      : (parsedForView as Record<string, any> | null);

  const pdfBase64 = useMemo(
    () => extractPdfProfileBase64(responseData?.result?.pdf_profile_file),
    [responseData]
  );

  const showPdfProfileToolbar =
    (props.serviceArn === RC_VERIFICATION_ADVANCE_ARN || props.serviceArn === RC_REVERSE_VERIFICATION_ARN) &&
    pdfBase64 !== null &&
    (props.isPostpaidClient === true ||
      String(responseData?.metadata?.billable ?? "").toUpperCase() === "Y");

  const pdfDownloadFileName = useMemo(() => {
    if (!showPdfProfileToolbar) return "document.pdf";
    const reg = responseData?.result?.rc_registration_number;
    return typeof reg === "string" && reg.trim()
      ? `${reg.trim()}.pdf`
      : "document.pdf";
  }, [showPdfProfileToolbar, responseData?.result?.rc_registration_number]);

  const downloadPdfProfile = () => {
    if (!pdfBase64) return;
    try {
      const binary = atob(pdfBase64);
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = sanitizePdfDownloadFilename(pdfDownloadFileName);
      a.click();
      URL.revokeObjectURL(url);
      props.toastTimeout(true, "success", "Downloaded PDF file");
    } catch {
      props.toastTimeout(true, "error", "Could not build PDF file");
    }
  };


  const stampPreviewUrl: string | null =
    props.stampCertificateMode && responseData
      ? (() => {
          const top =
            (isHttpUrlString(responseData.stampCertificateUrl) &&
              responseData.stampCertificateUrl.trim()) ||
            (isHttpUrlString(responseData.stampWithAttachment) &&
              responseData.stampWithAttachment.trim()) ||
            null;
          if (top) return top;
          const first = Array.isArray(responseData.orderItems)
            ? responseData.orderItems[0]
            : null;
          if (first && typeof first === "object") {
            const row = first as Record<string, unknown>;
            if (isHttpUrlString(row.stampCertificateUrl))
              return row.stampCertificateUrl.trim();
            if (isHttpUrlString(row.stampWithAttachment))
              return row.stampWithAttachment.trim();
          }
          return null;
        })()
      : null;

  /** Avoid duplicate iframe/link for the same URL when hero preview is shown. */
  const formFieldsSource =
    props.stampCertificateMode && stampPreviewUrl && responseData
      ? (() => {
          const next = { ...responseData };
          delete next.stampCertificateUrl;
          delete next.stampWithAttachment;
          delete next.stamp_certificate_url;
          delete next.stamp_with_attachment;
          if (Array.isArray(next.orderItems) && next.orderItems[0]) {
            next.orderItems = next.orderItems.map((item: any, idx: number) => {
              if (idx !== 0 || !item || typeof item !== "object") return item;
              const copy = { ...item };
              delete copy.stampCertificateUrl;
              delete copy.stampWithAttachment;
              delete copy.stamp_certificate_url;
              delete copy.stamp_with_attachment;
              return copy;
            });
          }
          return next;
        })()
      : responseData;

  const copyText = async () => {
    const response = responseBoxRef.current?.innerText ?? "";
    await navigator.clipboard.writeText(response);
    props.toastTimeout(true, "success", "Copied!");
  };

  const toggleAccordion = (accordionId: string) => {
    setOpenAccordions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(accordionId)) {
        newSet.delete(accordionId);
      } else {
        newSet.add(accordionId);
      }
      return newSet;
    });
  };

  const breakLongValue = (value: string, maxLength = 40) => {
    if (typeof value !== "string") return value;
    const regex = new RegExp(`.{1,${maxLength}}`, "g");
    return value.match(regex)?.map((chunk, idx) => (
      <React.Fragment key={idx}>
        {chunk}
        <br />
      </React.Fragment>
    ));
  };

  const renderFormFields = (obj: any, prefix = "") => {
    if (!obj || typeof obj !== "object" || Object.keys(obj).length === 0) {
      return <div className="text-muted">No data available</div>;
    }

    return Object.entries(obj).map(([key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const prettified = prettifyKey(key);

      // E-stamp certificate URL — hero preview when stampCertificateMode; else embed
      if (
        (key === "stampCertificateUrl" ||
          key === "stampWithAttachment" ||
          key === "stamp_certificate_url" ||
          key === "stamp_with_attachment") &&
        typeof value === "string" &&
        /^https?:\/\//i.test(value)
      ) {
        if (props.stampCertificateMode) {
          return (
            <div
              className="d-flex flex-column align-items-stretch mb-2"
              key={fullKey}
              style={{ fontSize: "12px" }}
            >
              <div className="fw-semibold text-secondary mb-1">{prettified}</div>
              <a
                {...testIdProps(`response-box-open-link-${fullKey.replace(/\./g, "-")}`)}
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="small"
              >
                Open link in new tab
              </a>
            </div>
          );
        }
        const useImg = isLikelyImageUrl(value);
        return (
          <div
            className="d-flex flex-column align-items-stretch mb-3"
            key={fullKey}
            style={{ fontSize: "12px" }}
          >
            <div className="fw-semibold text-secondary mb-1">{prettified}:</div>
            {useImg ? (
              <img
                src={value}
                alt="E-stamp certificate"
                style={{
                  width: "100%",
                  maxHeight: "480px",
                  objectFit: "contain",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  background: "#f9fafb",
                }}
              />
            ) : (
              <iframe
                src={value}
                title="E-stamp certificate"
                style={{
                  width: "100%",
                  minHeight: "420px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                }}
              />
            )}
            <a
              {...testIdProps(`response-box-stamp-link-${fullKey.replace(/\./g, "-")}`)}
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2"
            >
              Open stamp in new tab
            </a>
          </div>
        );
      }

      // Show image if base64 user_image
      if (
        key.toLowerCase().includes("user_image") &&
        typeof value === "string"
      ) {
        return (
          <div
            className="d-flex flex-column align-items-start mb-3"
            key={fullKey}
            style={{ fontSize: "12px" }}
          >
            <div className="fw-semibold text-secondary mb-1">{prettified}:</div>
            <img
              src={`data:image/jpeg;base64,${value}`}
              alt="User"
              style={{
                maxWidth: "100%",
                borderRadius: "6px",
                border: "1px solid #ccc",
              }}
            />
          </div>
        );
      }

      if (
        key === "pdf_profile_file" &&
        typeof value === "string" &&
        showPdfProfileToolbar
      ) {
        return (
          <div
            className="d-flex flex-column mb-2 border-bottom pb-2"
            key={fullKey}
            style={{ fontSize: "12px" }}
          >
            <div className="fw-semibold text-secondary mb-1">{prettified}:</div>
            <div className="text-muted small">
              PDF data included in this response.  — switch to Dev tab and use Download PDF.
            </div>
          </div>
        );
      }

      // Handle arrays (e.g., list of addresses, categories)
      if (Array.isArray(value)) {
        if (value.length === 0) return null;

        // Decide if array contains primitives (strings/numbers/booleans)
        const isPrimitiveArray = value.every(
          (item) =>
            item === null ||
            item === undefined ||
            ["string", "number", "boolean"].includes(typeof item)
        );

        return (
          <div key={fullKey} className="mb-3">
            <div
              className="fw-bold text-success mb-1"
              style={{ fontSize: "13px" }}
            >
              {prettified}
            </div>

            {isPrimitiveArray ? (
              <div className="ps-2 border-start border-2 border-success">
                {value.map((item, idx) => (
                  <div
                    key={`${fullKey}-${idx}`}
                    className="d-flex justify-content-between border-bottom pb-1 mb-2"
                    style={{ fontSize: "12px" }}
                  >
                    {value.length > 1 && (
                      <div className="fw-semibold text-secondary me-2">
                        {prettified} #{idx + 1}
                      </div>
                    )}
                    <div className="text-dark text-end">
                      {item !== null && item !== undefined
                        ? breakLongValue(String(item), 40)
                        : "-"}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              value.map((item, idx) => (
                <div
                  className="mb-2 ps-2 border-start border-2 border-success"
                  key={`${fullKey}-${idx}`}
                >
                  {value.length > 1 && (
                    <div
                      className="fw-semibold text-secondary mb-1"
                      style={{ fontSize: "12px" }}
                    >
                      {prettified} #{idx + 1}
                    </div>
                  )}
                  {renderFormFields(item, "")}
                </div>
              ))
            )}
          </div>
        );
      }

      // Nested object
      if (value && typeof value === "object") {
        const id = `accordion-${fullKey.replace(/\./g, "-")}`;
        const isOpen = openAccordions.has(id);
        return (
          <div className="accordion mb-2" key={fullKey}>
            <div className="accordion-item">
              <h2 className="accordion-header" id={`${id}-header`}>
                <button
                  className={`accordion-button ${isOpen ? "" : "collapsed"}`}
                  type="button"
                  onClick={() => toggleAccordion(id)}
                  aria-expanded={isOpen}
                  aria-controls={`${id}-collapse`}
                  {...testIdProps(`${id}-toggle`)}
                  style={{
                    fontSize: "14px",
                    background: "#D5FFEA",
                    color: "#008056",
                    fontWeight: "inherit",
                    textTransform: "capitalize",
                  }}
                >
                  {prettified}
                </button>
              </h2>
              <div
                id={`${id}-collapse`}
                className={`accordion-collapse ${isOpen ? "show" : "collapse"}`}
                aria-labelledby={`${id}-header`}
              >
                <div className="accordion-body">
                  {renderFormFields(value, fullKey)}
                </div>
              </div>
            </div>
          </div>
        );
      }

      // Fallback: string/primitive
      return (
        <div
          className="d-flex justify-content-between border-bottom pb-1 mb-2"
          key={fullKey}
          style={{ fontSize: "12px", textTransform: "capitalize" }}
        >
          <div className="fw-semibold text-secondary">{prettifyKey(key)}:</div>
          <div className="text-dark text-end">
            {value !== null && value !== undefined
              ? breakLongValue(String(value), 40)
              : "-"}
          </div>
        </div>
      );
    });
  };

  const renderDevCode = () => {
    return (
      <>
        <div ref={responseBoxRef} className="mt-3">
          {!hasParsedResponseData && props.errorResponse ? (
            <div
              style={{
                height: "309px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 24px",
                color: "#6b7280",
                textAlign: "center",
                gap: "16px",
              }}
            >
              <svg
                width="48"
                height="48"
                fill="none"
                viewBox="0 0 48 48"
                style={{ flexShrink: 0 }}
              >
                <circle cx="24" cy="24" r="24" fill="#f3f4f6" />
                <path
                  d="M24 14v12m0 4h.01"
                  stroke="#9ca3af"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div style={{ fontWeight: 600, fontSize: "1.15rem" }}>
                {isWalletBalanceError(props.errorResponse)
                  ? props.errorResponse
                  : "No Data Available"}
              </div>
              {!isWalletBalanceError(props.errorResponse) && (
                <div style={{ fontSize: "1rem", color: "#9ca3af" }}>
                  Please check back later or contact support for more
                  information.
                </div>
              )}
            </div>
          ) : (
            <div className={Styles.response}>
              <JSONPretty
                id="json-pretty"
                theme={themes}
                data={parsedForView}
              />
            </div>
          )}
        </div>

        {hasParsedResponseData && (
          <div className="d-flex flex-row flex-wrap gap-2 justify-content-end align-items-center mt-2">
            {showPdfProfileToolbar && (
              <button
                type="button"
                className={Styles.copyButton}
                {...testIdProps("response-box-download-pdf")}
                style={{
                  width: "auto",
                  minWidth: "72px",
                  paddingLeft: "8px",
                  paddingRight: "8px",
                }}
                onClick={downloadPdfProfile}
              >
                Download PDF
              </button>
            )}
            <button
              type="button"
              className={Styles.copyButton}
              {...testIdProps("response-box-copy")}
              onClick={copyText}
            >
              Copy
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <main
      className="bg-light rounded p-2"
      style={{ maxWidth: 500, marginLeft: "10px", height: "100%" }}
    >
      <section className="d-flex justify-content-between align-items-center mb-2 border-bottom border-black-50 pb-2">
        <div className="fw-bold fs-5 text-dark">Result</div>
        <div className="d-flex align-items-center gap-2">
          <div className="d-flex btn-group-sm border rounded overflow-hidden">
            <button
              type="button"
              className={`btn ${
                activeTab === "form" ? "btn-success" : "btn-light text-muted"
              }`}
              onClick={() => setActiveTab("form")}
              {...testIdProps("response-box-tab-form")}
            >
              Form
            </button>
            <button
              type="button"
              className={`btn ${
                activeTab === "dev" ? "btn-success" : "btn-light text-muted"
              }`}
              onClick={() => setActiveTab("dev")}
              {...testIdProps("response-box-tab-dev")}
            >
              Dev
            </button>
          </div>
        </div>
      </section>

      <div
        style={{
          overflowY: "auto",
          width: 480,
          minHeight: 400,
          maxHeight: "520px",
        }}
      >

        {activeTab === "form" ? (
          hasParsedResponseData ? (
            <>
              {props.stampCertificateMode && stampPreviewUrl ? (
                <div
                  className="mb-3 pb-3 border-bottom"
                  style={{ borderColor: "#e5e7eb" }}
                >
                  <div
                    className="fw-bold text-dark mb-2"
                    style={{ fontSize: "15px" }}
                  >
                    E-stamp certificate
                  </div>
                  {isLikelyImageUrl(stampPreviewUrl) ? (
                    <img
                      src={stampPreviewUrl}
                      alt="E-stamp certificate preview"
                      style={{
                        width: "100%",
                        maxHeight: "440px",
                        objectFit: "contain",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        background: "#f9fafb",
                      }}
                    />
                  ) : (
                    <iframe
                      src={stampPreviewUrl}
                      title="E-stamp certificate preview"
                      style={{
                        width: "100%",
                        minHeight: "400px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        background: "#fff",
                      }}
                    />
                  )}
                  <a
                    href={stampPreviewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-inline-block mt-2 small fw-semibold"
                    {...testIdProps("response-box-stamp-preview-link")}
                  >
                    Open full document in new tab
                  </a>
                </div>
              ) : null}
              {renderFormFields(formFieldsSource)}
            </>
          ) : (
            <div
              style={{
                height: "340px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 24px",
                color: "#6b7280",
                textAlign: "center",
                gap: "16px",
              }}
            >
              <svg
                width="48"
                height="48"
                fill="none"
                viewBox="0 0 48 48"
                style={{ flexShrink: 0 }}
              >
                <circle cx="24" cy="24" r="24" fill="#f3f4f6" />
                <path
                  d="M24 14v12m0 4h.01"
                  stroke="#9ca3af"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div style={{ fontWeight: 600, fontSize: "1.15rem" }}>
                {props.errorResponse && isWalletBalanceError(props.errorResponse)
                  ? props.errorResponse
                  : "No Data Available"}
              </div>
              {!(props.errorResponse && isWalletBalanceError(props.errorResponse)) && (
                <div style={{ fontSize: "1rem", color: "#9ca3af" }}>
                  Please check back later or contact support for more
                  information.
                </div>
              )}
            </div>
          )
        ) : (
          renderDevCode()
        )}
      </div>
    </main>
  );
}

export default ResponseBox;
